import { NavigateFunction } from 'react-router'
import { Dispatch } from '@reduxjs/toolkit'

import { ExchangesTab, MY_EXCHANGES } from '@/constants'
import {
    NotificationCenterAction,
    NotificationCenterActionCategory,
    NotificationCenterCategory,
} from '@/entities/NotificationCenter/NotificationCenter'
import {
    endEventLoggerApiCall,
    startEventLoggerApiCall,
} from '@/entities/NotificationCenter/NotificationCenterService'
import {
    getExchange as getExchangeApiCall,
    getExchangesOfferedByMe as getExchangesOfferedByMeApiCall,
    getExchangesOfferedForMe as getExchangesOfferedForMeApiCall,
} from '@/helpers/api/exchangesApi'
import {
    declineShiftExchange,
    deleteShiftExchange,
    getShiftExchange as getShiftExchangeApiCall,
    getShiftExchangesInDateRange,
    patchApproveShiftExchange,
    postShiftExchange,
} from '@/helpers/api-ts/shiftExchanges'
import { getErrorResponse } from '@/helpers/getErrorResponse'
import { getErrorStatus } from '@/helpers/getErrorStatus'
import { getEventLogTrackerData } from '@/helpers/getEventLogTrackerData'
import { trans } from '@/helpers/trans'
import { ExchangeResource } from '@/models'
import {
    EXCHANGE_SHIFTS_ACCEPT_SHIFT_EXCHANGE_FAILURE,
    EXCHANGE_SHIFTS_ACCEPT_SHIFT_EXCHANGE_REQUEST,
    EXCHANGE_SHIFTS_ACCEPT_SHIFT_EXCHANGE_SUCCESS,
    EXCHANGE_SHIFTS_CANCEL_SHIFT_EXCHANGE_FAILURE,
    EXCHANGE_SHIFTS_CANCEL_SHIFT_EXCHANGE_REQUEST,
    EXCHANGE_SHIFTS_CANCEL_SHIFT_EXCHANGE_SUCCESS,
    EXCHANGE_SHIFTS_FAILURE,
    EXCHANGE_SHIFTS_GET_SHIFT_EXCHANGE_FAILURE,
    EXCHANGE_SHIFTS_GET_SHIFT_EXCHANGE_REQUEST,
    EXCHANGE_SHIFTS_GET_SHIFT_EXCHANGE_SUCCESS,
    EXCHANGE_SHIFTS_GET_SHIFT_EXCHANGES_FAILURE,
    EXCHANGE_SHIFTS_GET_SHIFT_EXCHANGES_REQUEST,
    EXCHANGE_SHIFTS_GET_SHIFT_EXCHANGES_SUCCESS,
    EXCHANGE_SHIFTS_OPEN_SUCCESS,
    EXCHANGE_SHIFTS_PLANNED_SUCCESS,
    EXCHANGE_SHIFTS_REQUEST,
    EXCHANGE_SHIFTS_SUBMIT_SHIFT_EXCHANGE_FAILURE,
    EXCHANGE_SHIFTS_SUBMIT_SHIFT_EXCHANGE_REQUEST,
    EXCHANGE_SHIFTS_SUBMIT_SHIFT_EXCHANGE_SUCCESS,
    EXCHANGE_SUCCESS,
    SET_LOADING,
} from '@/redux/actionTypes'
import { setToast } from '@/redux/toasts/toastsReducer'
import { ToastType } from '@/types'

import { ExchangesAction } from './exchangesReducer'

const exchangeIncludes = [
    'shift',
    'shift.shiftPlannings',
    'shift.shiftPlannings.user',
    'shift.department',
    'shift.department.group',
    'shift.tracks',
    'replacementUsers',
    'owner',
]

const shiftExchangesIncludes = [
    'shiftPlanning1',
    'shiftPlanning2',
    'shiftPlanning1.user',
    'shiftPlanning2.user',
    'shiftPlanning1.shift',
    'shiftPlanning2.shift',
    'shiftPlanning1.shift.shiftPlannings',
    'shiftPlanning2.shift.shiftPlannings',
    'shiftPlanning1.shift.department',
    'shiftPlanning2.shift.department',
    'shiftPlanning1.shift.department.group',
    'shiftPlanning2.shift.department.group',
]

export const getExchangesOfferedByMe = () => async (dispatch: Dispatch): Promise<void> => {
    dispatch({ type: SET_LOADING, loading: true })
    dispatch({ type: EXCHANGE_SHIFTS_REQUEST })

    try {
        const response = await getExchangesOfferedByMeApiCall(exchangeIncludes)

        dispatch({
            type: EXCHANGE_SHIFTS_PLANNED_SUCCESS,
            shiftsOfferedByMe: response.data || [],
        })
    } catch (error) {
        dispatch({ type: EXCHANGE_SHIFTS_FAILURE, errors: [getErrorResponse(error)] })
    }

    dispatch({ type: SET_LOADING, loading: false })
}

export const getExchangesOfferedForMe = () => async (dispatch: Dispatch): Promise<void> => {
    dispatch({ type: EXCHANGE_SHIFTS_REQUEST })
    dispatch({ type: SET_LOADING, loading: true })

    try {
        const response = await getExchangesOfferedForMeApiCall(exchangeIncludes)

        dispatch({
            type: EXCHANGE_SHIFTS_OPEN_SUCCESS,
            shiftsOfferedForMe: response.data || [],
        })
    } catch (error) {
        dispatch({ type: EXCHANGE_SHIFTS_FAILURE, errors: [getErrorResponse(error)] })
    }

    dispatch({ type: SET_LOADING, loading: false })
}

export const getExchange = (
    exchange: string,
    navigate: NavigateFunction,
) => async (dispatch: Dispatch): Promise<void> => {
    dispatch({ type: SET_LOADING, loading: true })
    dispatch({ type: EXCHANGE_SHIFTS_REQUEST })

    try {
        const includes = [
            'shift',
            'shift.department',
            'shift.department.group',
            'shift.tracks',
            'shift.shiftPlannings',
            'shift.shiftPlannings.user',
            'shift.plannedUsers',
            'replacementUsers',
            'owner',
        ]

        const response = await getExchangeApiCall(includes, exchange)

        dispatch({
            type: EXCHANGE_SUCCESS,
            exchange: response.data,
        })
    } catch (error) {
        const errorResponse = getErrorResponse(error)

        dispatch({ type: EXCHANGE_SHIFTS_FAILURE, errors: [errorResponse] })

        // The exchange was taken over or withdrawn. Replace (not push) so Back does not return to the dead URL.
        if (getErrorStatus(errorResponse) === 404) {
            dispatch(setToast({ text: trans('pages.exchanges.exchangeNoLongerOffered'), type: ToastType.warning }))
            navigate(`/${trans('nav.exchanges.link')}`, { replace: true })
        }
    }

    dispatch({ type: SET_LOADING, loading: false })
}

export const setExchange = (exchange: ExchangeResource | null): ExchangesAction => ({ type: EXCHANGE_SUCCESS, exchange })

export const getExchangesTabLink = (tab: ExchangesTab): string => {
    const tabLink = tab === MY_EXCHANGES ? trans('nav.exchanges.myExchanges.link') : trans('nav.exchanges.offeredExchanges.link')

    return `/${trans('nav.exchanges.link')}/${tabLink}`
}

export const getShiftExchanges = () => async (dispatch: Dispatch): Promise<void> => {
    dispatch({ type: EXCHANGE_SHIFTS_GET_SHIFT_EXCHANGES_REQUEST })

    try {
        const shiftExchanges = await getShiftExchangesInDateRange(shiftExchangesIncludes)

        dispatch({ type: EXCHANGE_SHIFTS_GET_SHIFT_EXCHANGES_SUCCESS, shiftExchanges })
    } catch (error) {
        console.error('[fetchShiftExchanges]', error)
        dispatch({ type: EXCHANGE_SHIFTS_GET_SHIFT_EXCHANGES_FAILURE })
    }
}

export const getShiftExchange = (exchangeId: string) => async (dispatch: Dispatch): Promise<void> => {
    dispatch({ type: EXCHANGE_SHIFTS_GET_SHIFT_EXCHANGE_REQUEST })

    try {
        const shiftExchange = await getShiftExchangeApiCall(
            exchangeId,
            [
                'shiftPlanning1',
                'shiftPlanning2',
                'shiftPlanning1.user',
                'shiftPlanning2.user',
                'shiftPlanning1.shift',
                'shiftPlanning2.shift',
                'shiftPlanning1.shift.shiftPlannings',
                'shiftPlanning2.shift.shiftPlannings',
                'shiftPlanning1.shift.shiftPlannings.user',
                'shiftPlanning2.shift.shiftPlannings.user',
                'shiftPlanning1.shift.department',
                'shiftPlanning2.shift.department',
                'shiftPlanning1.shift.department.group',
                'shiftPlanning2.shift.department.group',
            ],
        )

        dispatch({ type: EXCHANGE_SHIFTS_GET_SHIFT_EXCHANGE_SUCCESS, shiftExchange })
    } catch (error) {
        console.error('[fetchShiftExchange]', error)
        dispatch({ type: EXCHANGE_SHIFTS_GET_SHIFT_EXCHANGE_FAILURE })
    }
}

export const cancelShiftExchange = (
    exchangeId: string,
    navigate: NavigateFunction,
    forMe?: boolean,
) => async (dispatch: Dispatch): Promise<void> => {
    dispatch({ type: EXCHANGE_SHIFTS_CANCEL_SHIFT_EXCHANGE_REQUEST })

    const eventLogTrackerData = getEventLogTrackerData(await startEventLoggerApiCall(
        NotificationCenterCategory.SHIFT_EXCHANGE,
        NotificationCenterAction.UPDATED,
        [NotificationCenterActionCategory.SHIFT_EXCHANGE_UPDATED],
    ))

    try {
        await (forMe ? declineShiftExchange(exchangeId, eventLogTrackerData) : deleteShiftExchange(exchangeId))
        const translation = trans(forMe ? 'pages.exchanges.denyShiftExchangeSuccess' : 'pages.exchanges.cancelShiftExchangeSuccess')

        dispatch(setToast({ text: translation, type: ToastType.pass }))
        dispatch({ type: EXCHANGE_SHIFTS_CANCEL_SHIFT_EXCHANGE_SUCCESS })
        navigate(`/${trans('nav.exchanges.link')}`)
    } catch (error) {
        console.error('[cancelShiftExchange]', error)
        dispatch({ type: EXCHANGE_SHIFTS_CANCEL_SHIFT_EXCHANGE_FAILURE })
    } finally {
        await endEventLoggerApiCall(eventLogTrackerData)
    }
}

export const acceptShiftExchange = (
    exchangeId: string,
    navigate: NavigateFunction,
) => async (dispatch: Dispatch): Promise<void> => {
    dispatch({ type: EXCHANGE_SHIFTS_ACCEPT_SHIFT_EXCHANGE_REQUEST })

    const eventLogTrackerData = getEventLogTrackerData(await startEventLoggerApiCall(
        NotificationCenterCategory.SHIFT_EXCHANGE,
        NotificationCenterAction.UPDATED,
        [NotificationCenterActionCategory.SHIFT_EXCHANGE_UPDATED],
    ))

    try {
        const shiftExchange = await patchApproveShiftExchange(exchangeId, eventLogTrackerData)
        dispatch(setToast({ text: trans('pages.exchanges.acceptShiftExchangeSuccess'), type: ToastType.pass }))
        dispatch({ type: EXCHANGE_SHIFTS_ACCEPT_SHIFT_EXCHANGE_SUCCESS, shiftExchange })
        navigate(`/${trans('nav.exchanges.link')}`)
    } catch (error) {
        console.error('[acceptShiftExchange]', error)
        dispatch({ type: EXCHANGE_SHIFTS_ACCEPT_SHIFT_EXCHANGE_FAILURE })
    } finally {
        await endEventLoggerApiCall(eventLogTrackerData)
    }
}

export const submitShiftExchange = (
    shiftPlanningId: string,
    shiftPlanning2Id: string,
    navigate: NavigateFunction,
) => async (dispatch: Dispatch): Promise<void> => {
    dispatch({ type: EXCHANGE_SHIFTS_SUBMIT_SHIFT_EXCHANGE_REQUEST })

    const eventLogTrackerData = getEventLogTrackerData(await startEventLoggerApiCall(
        NotificationCenterCategory.SHIFT_EXCHANGE,
        NotificationCenterAction.CREATED,
        [NotificationCenterActionCategory.SHIFT_EXCHANGE_CREATED],
    ))

    try {
        await postShiftExchange(shiftPlanningId, shiftPlanning2Id, eventLogTrackerData)
        dispatch(setToast({ text: trans('pages.exchanges.submitShiftExchangeSuccess'), type: ToastType.pass }))
        dispatch({ type: EXCHANGE_SHIFTS_SUBMIT_SHIFT_EXCHANGE_SUCCESS })
        navigate(`/${trans('nav.exchanges.link')}`)
    } catch (error) {
        console.error('[submitShiftExchange]', error)
        dispatch({ type: EXCHANGE_SHIFTS_SUBMIT_SHIFT_EXCHANGE_FAILURE })
    } finally {
        await endEventLoggerApiCall(eventLogTrackerData)
    }
}
